import Backbone from 'backbone';
import $ from 'jquery';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingCollection from 'BenchlingCollection';
import * as benchlingUtil from 'BenchlingUtil';
import * as context from 'Context';
import {maybeLogErrorResponseToSentry} from 'util/RequestCorrelation';
import {Constructor} from 'util/Types';

// Shape shared by Backbone Models and Collections that participate in batching. Only the
// duck-typed subset actually used by the helpers below is declared; Backbone.Model does not
// statically declare `batchName`, so callers of the batch helpers must provide it themselves
// (e.g. SequencePrimer, Translation, Annotation, and their collections).
interface BatchTriggerer {
  batchName(): string;
  trigger(eventName: string): unknown;
}

interface DebounceOptions {
  namespace: string;
  wait: number;
}

interface SingletonOptions {
  namespace: string;
}

interface BackboneSyncOptions extends JQueryAjaxSettings {
  debounce?: DebounceOptions | null;
  fileUpdate?: boolean;
  respondDiff?: boolean;
  singleton?: SingletonOptions | null;
  skipFileUpdateXHRCount?: boolean;
  skipTimeoutXHR?: boolean;
}

interface SuppressibleXHR extends JQueryXHR {
  __suppress_notification?: boolean;
}

interface DebouncedSyncEntry {
  // Always assigned in the same statement that constructs (or replaces) this map entry below, so
  // it is never actually absent at read time; keeping this non-optional means a broken invariant
  // becomes a compile error instead of silently leaving a promise pending.
  deferred: JQueryDeferred<unknown>;
  debouncedSync: (
    method: string,
    model: Backbone.Model | Backbone.Collection<Backbone.Model>,
    options: BackboneSyncOptions
  ) => void;
}

export class SingletonModel extends Backbone.Model {
  static _instance: SingletonModel | undefined;

  static initClass(): void {
    this._instance = undefined;
  }

  static get<TConstructor extends Constructor>(
    this: TConstructor,
    attributes?: ConstructorParameters<TConstructor>[0],
    options?: ConstructorParameters<TConstructor>[1]
  ): InstanceType<TConstructor> {
    // Every subclass gets its own `_instance` slot because `this` is the polymorphic static
    // receiver at the call site (e.g. `Digests.get()` reads/writes `Digests._instance`). The
    // bridge through `unknown` is required because the generic `Constructor` bound used for
    // `this` does not itself declare `_instance`.
    const ctor = this as unknown as typeof SingletonModel;
    if (ctor._instance == null) {
      ctor._instance = new this(attributes, options);
    }
    return ctor._instance as InstanceType<TConstructor>;
  }

  // This should be called from testSetup.ts for any singletons that may be modified between tests.
  // For now, this should be done on an individual basis, but ideally the backbone-extensions implementation
  // should bookkeep and reset all of them between tests.
  static resetForTests(): void {
    this._instance = undefined;
  }
}
SingletonModel.initClass();

export class SingletonCollection<
  M extends Backbone.Model,
  MAttrs extends Backbone.ObjectHash,
> extends BenchlingCollection<M, MAttrs> {
  // Declared with fully-instantiated type arguments (rather than the class's own `M`/`MAttrs`)
  // because static members cannot reference a class's instance-level type parameters.
  static _instance: SingletonCollection<Backbone.Model, Backbone.ObjectHash> | undefined;

  static initClass(): void {
    this._instance = undefined;
  }

  static get<TConstructor extends Constructor>(
    this: TConstructor,
    models?: ConstructorParameters<TConstructor>[0],
    options?: ConstructorParameters<TConstructor>[1]
  ): InstanceType<TConstructor> {
    // See SingletonModel.get above: same polymorphic-`this`/`_instance`-bridging rationale.
    const ctor = this as unknown as typeof SingletonCollection;
    if (ctor._instance == null) {
      ctor._instance = new this(models, options);
    }
    return ctor._instance as InstanceType<TConstructor>;
  }

  static resetForTests(): void {
    this._instance = undefined;
  }
}
SingletonCollection.initClass();

function getBatchField(triggerer: BatchTriggerer, trigger?: string, field?: string): string {
  if (trigger == null) {
    trigger = 'batch';
  }
  if (field == null) {
    field = '';
  }
  return `${trigger}-${triggerer.batchName()}${field ? `-${field}` : ''}`;
}

function batchModifyField(
  this: Backbone.Model,
  field: string,
  f: (this: Backbone.Model) => void,
  trigger?: string
): void {
  if (trigger == null) {
    trigger = 'batch';
  }
  // Narrowing to BatchTriggerer reflects the runtime invariant that anything calling
  // `batchModify` on a Model provides its own `batchName()` (e.g. SequencePrimer, Translation).
  const normField = getBatchField(this as unknown as BatchTriggerer, trigger, field);
  const info: Record<string, boolean> = {[normField]: true};
  context.using(info, this, f);

  if (!context.get(normField, false)) {
    this.get(field).trigger(trigger);
  }
}

function batchModifyModels(
  this: Backbone.Collection<Backbone.Model>,
  f: (this: Backbone.Collection<Backbone.Model>) => void,
  trigger?: string
): void {
  if (trigger == null) {
    trigger = 'batch';
  }
  // Narrowing to BatchTriggerer reflects the runtime invariant that anything calling
  // `batchModify` on a Collection provides its own `batchName()` (e.g. SequencePrimer's collection).
  batchModifyCollections([this as unknown as BatchTriggerer], this, f, trigger);
}

function batchModifyCollections<This>(
  collections: Array<BatchTriggerer>,
  caller: This,
  f: (this: This) => void,
  trigger?: string
): void {
  if (trigger == null) {
    trigger = 'batch';
  }
  const info: Record<string, boolean> = {};
  for (const collection of collections) {
    info[getBatchField(collection, trigger)] = true;
  }
  context.using(info, caller, f);

  for (const collection of collections) {
    const normField = getBatchField(collection, trigger);
    if (!context.get(normField, false)) {
      collection.trigger(trigger);
    }
  }
}

function batch(
  this: BatchCollection<Backbone.Model>,
  fn: (this: BatchCollection<Backbone.Model>) => void,
  trigger?: string
): () => void {
  if (trigger == null) {
    trigger = 'batch';
  }
  return this.listenToBatch(this, fn, trigger);
}

/**
 * Wraps `fn`, delaying calls made during a batch until the batch event fires.
 *
 * Unlike `{silent: true}`, batching affects only this handler. Unlike debouncing, it introduces
 * no timing delay. Event producers must wrap mutations in `batchModify` to establish the batching
 * context.
 */
function listenToBatch<M extends Backbone.Model>(
  this: Backbone.Events,
  collection: BatchCollection<M>,
  fn: (this: Backbone.Events) => void,
  trigger?: string
): () => void {
  if (trigger == null) {
    trigger = 'batch';
  }
  const normField = getBatchField(collection, trigger);

  let batchFlag = false;

  function newFn(this: Backbone.Events): unknown {
    // During a batch, record that this handler was invoked and defer the real call until the batch
    // event fires; outside a batch, run `fn` immediately.
    if (context.get(normField, false)) {
      batchFlag = true;
      return true;
    }
    batchFlag = false;
    return fn.call(this);
  }

  this.listenTo(collection, trigger, function (this: Backbone.Events): unknown {
    if (batchFlag) {
      return newFn.call(this);
    }
    return undefined;
  });
  return newFn;
}

function inBatch(this: BatchCollection<Backbone.Model>, trigger?: string): boolean {
  if (trigger == null) {
    trigger = 'batch';
  }
  return context.get(getBatchField(this, trigger), false);
}

Backbone.Collection.prototype.inBatch = inBatch;
Backbone.Collection.prototype.batch = batch;
Backbone.Collection.prototype.listenToBatch = listenToBatch;
Backbone.Model.prototype.listenToBatch = listenToBatch;
Backbone.View.prototype.listenToBatch = listenToBatch;
Backbone.Collection.prototype.batchModify = batchModifyModels;
Backbone.Model.prototype.batchModify = batchModifyField;
Backbone.batchModify = batchModifyCollections;

Backbone.Model.prototype.fastUnset = function (this: Backbone.Model, attr: string): Backbone.Model {
  return this.fastSet(attr, undefined);
};

Backbone.Model.prototype.fastSet = function (
  this: Backbone.Model,
  key: string | object,
  val?: unknown
): Backbone.Model {
  // No change events, no cloning, just setting on the model.
  let attrs: Record<string, unknown>;
  if (key == null) {
    return this;
  }
  if (typeof key === 'object') {
    attrs = key as Record<string, unknown>;
  } else {
    attrs = {[key]: val};
  }

  for (const attr of Object.keys(attrs)) {
    this.attributes[attr] = attrs[attr];
  }

  return this;
};

const normalSync = Backbone.sync;

let xhrCount = 0;
let longXHRCount = 0;
let fileUpdateXHRCount = 0;

function updateXHRCount(delta: 1 | -1, {fileUpdate, skipFileUpdateXHRCount}: BackboneSyncOptions): void {
  assert([+1, -1].includes(delta), 'updateXHRCount delta must be +1 or -1');
  xhrCount += delta;
  if (fileUpdate && !skipFileUpdateXHRCount) {
    fileUpdateXHRCount += delta;
  }
}

function timeoutXHR(xhr: JQueryPromise<unknown>): void {
  let timedOut = false;

  function uhohTimeout(): void {
    Backbone.trigger('long-sync', ++longXHRCount);
    timedOut = true;
  }

  const id = setTimeout(uhohTimeout, 5000, xhr);

  xhr.always(() => {
    if (timedOut) {
      --longXHRCount;
    }
    clearTimeout(id);
  });
}

Backbone.getXHRCount = () => xhrCount;

// Key: namespace hyphenated with Backbone model's unique client ID.
// Value: debounced sync function to call and Deferred object to return.
const debouncedSyncMap: Record<string, DebouncedSyncEntry> = {};
// Value: last XHR associated with the key.
const singletonMap: Record<string, SuppressibleXHR> = {};

function normalSyncWithSingleton(
  // This helper is always called as a free function, so the native sync receiver is `undefined`.
  this: void,
  method: string,
  model: Backbone.Model | Backbone.Collection<Backbone.Model>,
  options: BackboneSyncOptions
): JQueryPromise<unknown> {
  // The real Backbone.sync always resolves to a jqXHR (which supports `abort()`); the ambient
  // `JQueryPromise<unknown>` return type is widened for callers that don't need to abort.
  const xhr = normalSync.call(this, method, model, options) as SuppressibleXHR;
  // If the sync should be a singleton (only one request in flight),
  // abort the previous request.
  if (options.singleton != null) {
    // Only BenchlingCollection instances (not the base Backbone.Collection) assign `cid`; the
    // singleton/debounce features are only ever used with such instances or with Models.
    const key = `${options.singleton.namespace}-${(model as {cid: string}).cid}`;
    if (singletonMap[key] != null) {
      singletonMap[key].__suppress_notification = true;
      singletonMap[key].abort();
    }
    singletonMap[key] = xhr;
  }
  return xhr;
}

Backbone.sync = function (
  method: string,
  model: Backbone.Model | Backbone.Collection<Backbone.Model>,
  options?: BackboneSyncOptions
): JQueryPromise<unknown> {
  let deferred: JQueryDeferred<unknown> | undefined;
  let xhr: JQueryPromise<unknown>;
  if (options == null) {
    options = {};
  }
  _.defaults(options, {
    debounce: null,
    respondDiff: false,
    fileUpdate: false,
    singleton: null,
    skipFileUpdateXHRCount: false,
    skipTimeoutXHR: false,
  });

  if (!options.fileUpdate) {
    return normalSyncWithSingleton(method, model, options);
  }

  // If the operation is only asks for a diff from the server (and will apply the diff),
  // then there should be no ongoing fileUpdate XHRs.
  if (fileUpdateXHRCount > 0 && !options.skipFileUpdateXHRCount) {
    deferred = $.Deferred();
    const emptyPromise = deferred.promise();
    // Error notification will be handled by premature-sync.
    deferred.reject({__suppress_notification: true});
    Backbone.trigger('premature-sync');
    return emptyPromise;
  }

  // If the sync should be debounced, call the debounced sync function
  // and return the empty promise from debouncedSyncMap.
  if (options.debounce != null) {
    const key = `${options.debounce.namespace}-${(model as {cid: string}).cid}`;
    if (key in debouncedSyncMap) {
      debouncedSyncMap[key].deferred.reject({__suppress_notification: true});
      debouncedSyncMap[key].deferred = $.Deferred();
    } else {
      debouncedSyncMap[key] = {
        deferred: $.Deferred(),
        debouncedSync: debounce(
          (
            debounceMethod: string,
            debounceModel: Backbone.Model | Backbone.Collection<Backbone.Model>,
            debounceOptions: BackboneSyncOptions
          ) => {
            const entryDeferred = debouncedSyncMap[key].deferred;
            delete debouncedSyncMap[key];
            normalSyncWithSingleton(debounceMethod, debounceModel, debounceOptions)
              .done((...args: Array<unknown>) => entryDeferred.resolve(...args))
              .fail((...args: Array<unknown>) => entryDeferred.reject(...args));
          },
          options.debounce.wait
        ),
      };
    }
    xhr = debouncedSyncMap[key].deferred.promise();
    debouncedSyncMap[key].debouncedSync(method, model, options);
  } else {
    xhr = normalSyncWithSingleton(method, model, options);
  }

  if (!options.skipTimeoutXHR) {
    timeoutXHR(xhr);
  }
  updateXHRCount(+1, options);
  Backbone.trigger('syncing', xhr, xhrCount, longXHRCount);
  xhr
    .done(() => {
      updateXHRCount(-1, options);
      Backbone.trigger('good-sync', xhr, xhrCount, longXHRCount);
    })
    // Capture the failure arguments with a rest parameter rather than `arguments`. Because this is
    // an arrow function, `arguments` would resolve to the enclosing `Backbone.sync` call's
    // (method, model, options) instead of the actual failure args, which is exactly the bug this
    // replaces. jQuery invokes fail handlers with the rejected Deferred's value, or with
    // (jqXHR, textStatus, errorThrown) for a real XHR; both carry the `__suppress_notification`
    // flag set for superseded/aborted requests.
    .fail((...failArgs: Array<unknown>): void => {
      updateXHRCount(-1, options);
      // `shouldSuppressNotification` looks for `__suppress_notification` on either the rejected
      // Deferred value (`res`) or the jqXHR/ajaxOptions, matching how jQuery invokes fail handlers.
      const [res, ajaxOptions] = failArgs as [
        {__suppress_notification?: boolean} | undefined,
        {__suppress_notification?: boolean} | undefined,
      ];
      if (benchlingUtil.shouldSuppressNotification(res, ajaxOptions)) {
        return;
      }
      maybeLogErrorResponseToSentry(res);
      Backbone.trigger('fail-sync', xhr, xhrCount, longXHRCount);
    });
  return xhr;
};

// Read through a narrow structural view of the prototype (rather than the typed
// `Backbone.Model.prototype`) so `listenTo` resolves as an ordinary function value (its `this` is
// always supplied explicitly via `.apply(this, args)` below) instead of a class method reference
// with an implicit receiver. The callable signature mirrors the vendored `Backbone.Events#listenTo`
// declaration so the read-through stays precisely typed.
const modelPrototypeListenTo: {listenTo: (other: unknown, event: string, callback: Function) => unknown} =
  Backbone.Model.prototype;
const originalListenTo = modelPrototypeListenTo.listenTo;

Backbone.Model.prototype.listenTo = function (
  this: Backbone.Model,
  ...args: [other: unknown, event: string, callback: Function]
): unknown {
  const [, , callback] = args;
  assertAndContinue(callback != null, 'Callback for listenTo was not defined');
  return originalListenTo.apply(this, args);
};
